import { keyframes } from "@mui/system";

import React from "react";

import { Box, Slide, useMediaQuery } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";

// Onboarding motion: surfaces arrive with a long, soft settle and leave quickly,
// so the guest feels the sheet land but never waits on it to go.
const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";

const SHEET_ENTER_MS = 380;
const SHEET_EXIT_MS = 220;
const STEP_ENTER_MS = 320;
// Lets the drawer travel most of the way before its first page appears.
const STEP_DELAY_MS = 90;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

const rise = keyframes({
  from: { opacity: 0, transform: "translateY(12px)" },
  to: { opacity: 1, transform: "none" },
});

/** Props for a drawer or dialog sheet: timing, easing, none under reduced motion. */
export const useSheetMotion = () => {
  const reduceMotion = useMediaQuery(REDUCED_MOTION);
  return {
    transitionDuration: reduceMotion
      ? 0
      : { enter: SHEET_ENTER_MS, exit: SHEET_EXIT_MS },
    easing: { enter: EASE_OUT, exit: EASE_IN },
  };
};

/**
 * Fades and lifts one onboarding page in. Give it a new `key` per page so the
 * next page replays the entrance instead of swapping in place. No fill mode:
 * once settled it leaves no transform behind, which would otherwise trap the
 * fixed footer inside it.
 */
export const OnboardingStep = ({ children }: React.PropsWithChildren) => (
  <Box
    sx={{
      width: "100%",
      // Fills the drawer column so a short page can centre itself in it.
      flex: 1,
      display: "flex",
      flexDirection: "column",
      animation: `${rise} ${STEP_ENTER_MS}ms ${EASE_OUT} ${STEP_DELAY_MS}ms backwards`,
      [`@media ${REDUCED_MOTION}`]: { animation: "none" },
    }}
  >
    {children}
  </Box>
);

/** Dialog transition that rises from the bottom edge, for sheets on phones. */
export const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
